import { APIFieldType, Operator } from '@ardoq/api-types';
import { UNSET_OR_NOTAPPLICABLE_VALUE_LABEL } from '../../consts';
import { keyBy } from 'lodash';
import type {
  DashboardFilterOperatorButtonProps,
  DashboardFilterPillValueProps,
} from '@ardoq/table-filter-bar';
import type { CheckboxDashboardFilter, ListDashboardFilter } from '../../types';
import { toValuePopoverContentProps } from '../toValuePopoverContentProps';
import {
  type GetOperatorAndValuePropsParams,
  getValueProps,
  toListPopoverOption,
} from './utils';

export const getListFilterOperatorAndValueProps = ({
  filterPillUIState,
  dashboardFilter,
  updateDashboardFilterValues,
  requestDashboardFilterSuggestions,
  hasInaccessibleWidgets,
  i18nLocale,
}: GetOperatorAndValuePropsParams & {
  dashboardFilter: ListDashboardFilter | CheckboxDashboardFilter;
}): {
  operatorProps: DashboardFilterOperatorButtonProps;
  valueProps: DashboardFilterPillValueProps;
} => {
  const filterType = dashboardFilter.type;

  const onValueChange = (values: Array<string>) => {
    updateDashboardFilterValues(
      dashboardFilter.id,
      filterType,
      values.map(v => {
        if (v === UNSET_OR_NOTAPPLICABLE_VALUE_LABEL) {
          return {
            operator: Operator.IS_EMPTY,
            value: null,
          };
        }

        if (dashboardFilter.type === APIFieldType.CHECKBOX) {
          return {
            operator: Operator.EQUAL,
            value: v === 'true',
          };
        }

        return {
          operator: Operator.EQUAL,
          value: v,
        };
      })
    );
  };
  const optionsAsStrings = dashboardFilter.options.map(option => ({
    value:
      option.value === null
        ? UNSET_OR_NOTAPPLICABLE_VALUE_LABEL
        : String(option.value),
    label: option.label,
  }));
  const labelByValue = keyBy(optionsAsStrings || [], 'value');
  const selectedOptions = dashboardFilter.values.map(({ value }) => {
    const valueAsString =
      value === null ? UNSET_OR_NOTAPPLICABLE_VALUE_LABEL : String(value);

    return {
      value: valueAsString,
      label: labelByValue[valueAsString]?.label ?? valueAsString,
    };
  });

  const hasSelectableOptions = dashboardFilter.options.some(
    option => option.value !== null
  );

  const openValuePopover = () => {
    if (!filterPillUIState.isValuePopoverOpen && !hasSelectableOptions) {
      requestDashboardFilterSuggestions(dashboardFilter.id, '');
    }
    filterPillUIState.toggleIsValuePopoverOpen();
  };

  const valueProps = getValueProps({
    onClick: openValuePopover,
    filterPillUIState,
    i18nLocale,
    content: toValuePopoverContentProps(
      {
        type: 'list',
        groups: [
          {
            options: optionsAsStrings.map(option =>
              toListPopoverOption(
                option,
                selectedOptions.map(({ value }) => value),
                onValueChange
              )
            ),
          },
        ],
      },
      filterPillUIState,
      i18nLocale
    ),
    name: dashboardFilter.name,
    labelsForSelectedValues: selectedOptions.map(({ label }) => label),
    hasLinkedWidgets: dashboardFilter.linkCounts.total > 0,
    isInvalid: false,
    hasInaccessibleWidgets,
    clearFilter: () => onValueChange([]),
    onClose: filterPillUIState.toggleIsValuePopoverOpen,
  });
  return {
    operatorProps: {
      hide: true,
    },
    valueProps,
  };
};
